import { useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, Globe, Link2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../hooks/useToast';
import { HelpTooltip } from '../../components/common/HelpTooltip';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import {
  createBrandUrl,
  deleteBrandUrl,
  isValidBrandUrl,
  updateBrandUrl,
  useBrandUrls,
  type BrandUrl,
} from '../../features/brands/urls';

interface BrandUrlSectionProps {
  brandId: string;
}

/**
 * ブランド詳細ページの「ブランドURL」セクション。
 * 名前付きURL (公式サイト・キャンペーンLP等) を 1行=1URL のリストで管理する。
 * 登録したURLは静止画生成のテンプレート (ポスター等) で参照ページとして選択できる。
 */
export function BrandUrlSection({ brandId }: BrandUrlSectionProps) {
  const toast = useToast();
  const { confirm, dialog } = useConfirmDialog();
  const queryClient = useQueryClient();
  const { data, isLoading } = useBrandUrls(brandId);
  const urls = data ?? [];

  const [showAddModal, setShowAddModal] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['brand-urls', brandId] });
    queryClient.invalidateQueries({ queryKey: ['all-brand-urls'] });
  };

  const extractError = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
    (err instanceof Error ? err.message : fallback);

  const handleAdd = async (payload: { name: string; url: string }) => {
    try {
      await createBrandUrl(brandId, payload);
      invalidate();
      toast.success('URLを追加しました');
      return true;
    } catch (err: unknown) {
      toast.error(extractError(err, 'URLの追加に失敗しました'));
      return false;
    }
  };

  const handleUpdate = async (urlId: string, payload: { name: string; url: string }) => {
    try {
      await updateBrandUrl(brandId, urlId, payload);
      invalidate();
      toast.success('URLを更新しました');
      return true;
    } catch (err: unknown) {
      toast.error(extractError(err, 'URLの更新に失敗しました'));
      return false;
    }
  };

  const handleDelete = async (urlId: string) => {
    if (!(await confirm({ title: 'このURLを削除しますか？', confirmLabel: '削除する' }))) return;
    try {
      await deleteBrandUrl(brandId, urlId);
      invalidate();
      toast.success('URLを削除しました');
    } catch (err: unknown) {
      toast.error(extractError(err, 'URLの削除に失敗しました'));
    }
  };

  return (
    <>
      {dialog}
    <div className="card brand-url-card">
      <h2 className="card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <Link2 size={18} aria-hidden="true" className="card-title-icon" style={{ marginRight: 0 }} />
        ブランドURL
        <HelpTooltip ariaLabel="ブランドURLのヘルプ">
          ブランドのホームページやキャンペーンLPのURLを名前を付けて登録します。静止画生成のテンプレート
          (ポスター等) で参照ページとして選択でき、生成時にページ全体のキャプチャが参照画像に使われます。
        </HelpTooltip>
      </h2>

      {isLoading ? (
        <div className="loading-stats">読み込み中...</div>
      ) : (
        <div className="brand-url-list">
          {urls.length === 0 && <p className="brand-empty-note">URLが登録されていません。</p>}
          {urls.map((item) => (
            <BrandUrlRow
              key={item.id}
              item={item}
              onUpdate={(payload) => handleUpdate(item.id, payload)}
              onDelete={() => handleDelete(item.id)}
            />
          ))}

          {/* 追加はモーダルで行う (ページ上はボタンのみ) */}
          <div className="brand-url-add-row">
            <button
              type="button"
              className="brand-add-button"
              onClick={() => setShowAddModal(true)}
            >
              <Plus size={14} aria-hidden="true" />
              URLを追加
            </button>
          </div>
        </div>
      )}

      {showAddModal && (
        <BrandUrlAddModal
          onAdd={handleAdd}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
    </>
  );
}

/**
 * ブランドURL追加モーダル。共通の modal-overlay / modal-card クラスを利用する。
 */
function BrandUrlAddModal({
  onAdd,
  onClose,
}: {
  onAdd: (payload: { name: string; url: string }) => Promise<boolean>;
  onClose: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName) {
      toast.error('名前を入力してください');
      return;
    }
    if (!isValidBrandUrl(trimmedUrl)) {
      toast.error('http または https のURLを入力してください');
      return;
    }
    setSaving(true);
    try {
      const ok = await onAdd({ name: trimmedName, url: trimmedUrl });
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card modal-card--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">ブランドURLを追加</span>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        <form className="modal-body brand-url-modal-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="brand-url-input"
            placeholder="名前 (例: 公式サイト)"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <input
            type="url"
            className="brand-url-input"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <div className="brand-url-modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              キャンセル
            </button>
            <button
              type="submit"
              className="btn-modal"
              disabled={saving || !name.trim() || !url.trim()}
            >
              {saving ? '追加中…' : '追加'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

/**
 * ブランドURL1件の行。通常時は名前 + URLリンク、鉛筆で行ごと編集モードに切替。
 */
function BrandUrlRow({
  item,
  onUpdate,
  onDelete,
}: {
  item: BrandUrl;
  onUpdate: (payload: { name: string; url: string }) => Promise<boolean>;
  onDelete: () => void;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(item.name);
  const [draftUrl, setDraftUrl] = useState(item.url);

  const startEditing = () => {
    setDraftName(item.name);
    setDraftUrl(item.url);
    setEditing(true);
  };

  const commit = async () => {
    const name = draftName.trim();
    const url = draftUrl.trim();
    if (!name) {
      toast.error('名前を入力してください');
      return;
    }
    if (!isValidBrandUrl(url)) {
      toast.error('http または https のURLを入力してください');
      return;
    }
    if (name === item.name && url === item.url) {
      setEditing(false);
      return;
    }
    const ok = await onUpdate({ name, url });
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <div className="brand-url-row">
        <input
          type="text"
          className="brand-url-input brand-url-input--name"
          value={draftName}
          maxLength={100}
          placeholder="名前"
          onChange={(e) => setDraftName(e.target.value)}
        />
        <Globe size={15} aria-hidden="true" className="brand-url-globe" />
        <input
          type="url"
          className="brand-url-input brand-url-input--url"
          value={draftUrl}
          placeholder="https://example.com"
          onChange={(e) => setDraftUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        <button type="button" className="brand-url-icon-button" onClick={commit} title="保存" aria-label="保存">
          <Check size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="brand-url-icon-button"
          onClick={() => setEditing(false)}
          title="キャンセル"
          aria-label="キャンセル"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="brand-url-row">
      <span className="brand-url-name" title={item.name}>{item.name}</span>
      <Globe size={15} aria-hidden="true" className="brand-url-globe" />
      <a className="brand-url-link" href={item.url} target="_blank" rel="noopener noreferrer" title={item.url}>
        {item.url}
      </a>
      <button type="button" className="brand-url-icon-button" onClick={startEditing} title="編集" aria-label="編集">
        <Pencil size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="brand-url-icon-button brand-url-icon-button--danger"
        onClick={onDelete}
        title="削除"
        aria-label="削除"
      >
        <Trash2 size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
